import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FaImage, FaMinus, FaPlus, FaCheck, FaTimes } from 'react-icons/fa';
import { resolveLineFicha } from './OcLineCard';
import { VEREDICTO_META, formatCop } from './veredictoHelpers';
import formatAplicaciones from '../../../utils/formatAplicaciones';
import OcAprobarInsight from './OcAprobarInsight';

const authHeaders = (token) => ({ Authorization: `Bearer ${token}` });

/**
 * Vista pública móvil: el líder aprueba una OC desde el link de WhatsApp (sin login).
 */
const OrdenCompraAprobarPublic = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t') || '';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orden, setOrden] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // 'aprobada' | 'rechazada' | 'resuelta'
  const [showReject, setShowReject] = useState(false);
  const [showApprove, setShowApprove] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [observacionesAprobacion, setObservacionesAprobacion] = useState('');
  const [fotoGrande, setFotoGrande] = useState(null); // { url, nombre }

  const load = useCallback(async () => {
    if (!token) {
      setError('Este link no es válido. Pide uno nuevo por WhatsApp.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get('/api/compras/public/orden', {
        headers: authHeaders(token),
        params: { t: token },
      });
      setOrden(data.data);
      if (data.data?.ya_resuelta) {
        const est = data.data.estado;
        setDone(est === 'rechazada' ? 'rechazada' : est === 'aprobada' ? 'aprobada' : 'resuelta');
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'No se pudo cargar la orden';
      const code = err.response?.data?.code;
      if (code === 'OC_TOKEN_EXPIRED') {
        setError('Este link ya venció. Pide uno nuevo al enviar la orden otra vez.');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const lineas = orden?.detalle || [];
  const totalActivo = useMemo(() => {
    if (orden?.total_activo != null) return Number(orden.total_activo);
    return lineas
      .filter((l) => !l.excluida)
      .reduce((s, l) => s + (Number(l.valor_linea) || 0), 0);
  }, [orden, lineas]);

  const patchLinea = async (idDetalle, patch) => {
    if (!token || busy) return;
    setSavingId(idDetalle);
    try {
      const { data } = await axios.patch(
        '/api/compras/public/orden/lineas',
        { lineas: [{ id_detalle: idDetalle, ...patch }] },
        { headers: authHeaders(token) },
      );
      setOrden((prev) => ({
        ...prev,
        ...data.data,
        ya_resuelta: prev?.ya_resuelta,
        nombre_aprobador_sesion: prev?.nombre_aprobador_sesion,
        endeudamiento: prev?.endeudamiento,
      }));
    } catch (err) {
      window.alert(err.response?.data?.error || 'No se pudo guardar el cambio');
    } finally {
      setSavingId(null);
    }
  };

  const changeQty = (line, delta) => {
    if (line.excluida) return;
    const next = Math.max(1, Math.round((Number(line.cantidad) || 1) + delta));
    if (next === Number(line.cantidad)) return;
    patchLinea(line.id_detalle, { cantidad: next });
  };

  const setQty = (line, raw) => {
    if (line.excluida) return;
    const n = parseFloat(String(raw).replace(',', '.'));
    if (!Number.isFinite(n) || n < 0.001) return;
    if (Math.abs(n - Number(line.cantidad)) < 0.0005) return;
    patchLinea(line.id_detalle, { cantidad: n });
  };

  const toggleExcluir = (line) => {
    patchLinea(line.id_detalle, { excluida: !line.excluida });
  };

  const openAprobar = () => {
    if (busy) return;
    const activas = lineas.filter((l) => !l.excluida);
    if (!activas.length) {
      window.alert('Deja al menos un producto en la compra');
      return;
    }
    setShowApprove(true);
  };

  const confirmarAprobar = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const obs = observacionesAprobacion.trim();
      await axios.post(
        '/api/compras/public/orden/aprobar',
        { observaciones: obs || null },
        { headers: authHeaders(token) },
      );
      setDone('aprobada');
      setShowApprove(false);
    } catch (err) {
      window.alert(err.response?.data?.error || 'No se pudo aprobar');
    } finally {
      setBusy(false);
    }
  };

  const rechazar = async () => {
    if (busy) return;
    const m = motivo.trim();
    if (!m) {
      window.alert('Escribe el motivo del rechazo');
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        '/api/compras/public/orden/rechazar',
        { motivo: m },
        { headers: authHeaders(token) },
      );
      setDone('rechazada');
      setShowReject(false);
    } catch (err) {
      window.alert(err.response?.data?.error || 'No se pudo rechazar');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6">
        <p className="text-lg text-slate-600">Cargando orden…</p>
      </div>
    );
  }

  if (error && !orden) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 max-w-md w-full text-center">
          <p className="text-xl font-bold text-slate-800 mb-2">No se puede abrir</p>
          <p className="text-base text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  if (done) {
    const title =
      done === 'aprobada'
        ? 'Listo. Compra aprobada.'
        : done === 'rechazada'
          ? 'Orden rechazada.'
          : 'Esta orden ya fue resuelta.';
    const ok = done === 'aprobada';
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 max-w-md w-full text-center space-y-3">
          <div
            className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center ${
              ok ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'
            }`}
          >
            {ok ? <FaCheck className="text-2xl" /> : <FaTimes className="text-2xl" />}
          </div>
          <p className="text-2xl font-bold text-slate-900">{title}</p>
          <p className="text-base text-slate-600">Ya puedes cerrar WhatsApp.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 pb-36">
      <header className="bg-white border-b border-slate-200 px-4 pt-5 pb-4 sticky top-0 z-10">
        <p className="text-sm font-semibold text-emerald-700 uppercase tracking-wide">
          Orden para aprobar
        </p>
        <h1 className="text-2xl font-bold text-slate-900 mt-1">
          {orden?.numero_orden || 'Orden'}
        </h1>
        <p className="text-base text-slate-700 mt-1">
          {orden?.nombre_proveedor || 'Proveedor'}
        </p>
        {orden?.nombre_empleado && (
          <p className="text-sm text-slate-500 mt-0.5">
            Envió: <span className="font-semibold text-slate-700">{orden.nombre_empleado}</span>
          </p>
        )}
        <p className="text-3xl font-bold text-emerald-700 mt-3 tabular-nums">
          {formatCop(totalActivo)}
        </p>
        <p className="text-sm text-slate-500">Total estimado (productos activos)</p>

        {orden?.endeudamiento && (
          <div className="mt-4 grid grid-cols-2 gap-2">
            <div className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2.5">
              <p className="text-[11px] font-bold uppercase text-slate-500">Pagos prog. mes</p>
              <p className="text-lg font-black text-slate-900 tabular-nums mt-0.5 truncate">
                {formatCop(orden.endeudamiento.pagos_programados)}
              </p>
            </div>
            <div className="rounded-xl bg-rose-50 border border-rose-200 px-3 py-2.5">
              <p className="text-[11px] font-bold uppercase text-rose-700">Deuda total</p>
              <p className="text-lg font-black text-rose-800 tabular-nums mt-0.5 truncate">
                {formatCop(orden.endeudamiento.total_pendiente)}
              </p>
            </div>
          </div>
        )}
      </header>

      <main className="px-3 py-4 space-y-3 max-w-lg mx-auto">
        {lineas.map((line) => {
          const ficha = resolveLineFicha(line);
          const vMeta = VEREDICTO_META[line.veredicto] || VEREDICTO_META.posponer;
          const excluded = Boolean(line.excluida);
          const saving = savingId === line.id_detalle;
          const aplicacionesTxt = ficha.aplicaciones
            ? formatAplicaciones(ficha.aplicaciones)
            : null;

          return (
            <article
              key={line.id_detalle}
              className={`rounded-2xl border bg-white p-4 shadow-sm ${
                excluded ? 'border-slate-200 opacity-70' : 'border-slate-200'
              }`}
            >
              {/* 1. Thumbnail + nombre + badge */}
              <div className="flex gap-3">
                {ficha.imagen_url ? (
                  <button
                    type="button"
                    onClick={() => setFotoGrande({ url: ficha.imagen_url, nombre: ficha.nombre })}
                    className="w-20 h-20 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center overflow-hidden shrink-0 active:scale-[0.97]"
                    aria-label="Ampliar foto"
                  >
                    <img
                      src={ficha.imagen_url}
                      alt=""
                      className="w-full h-full object-contain p-1 pointer-events-none"
                    />
                  </button>
                ) : (
                  <div className="w-20 h-20 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center shrink-0">
                    <FaImage className="text-slate-300 text-xl" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <span className={`inline-block text-[11px] font-bold uppercase px-2 py-0.5 rounded-full ${vMeta.badge}`}>
                    {vMeta.label}
                  </span>
                  <p
                    className={`text-base font-bold text-slate-900 mt-1 leading-snug ${
                      excluded ? 'line-through text-slate-500' : ''
                    }`}
                  >
                    {ficha.nombre}
                  </p>
                  {excluded && (
                    <p className="text-sm font-semibold text-red-600 mt-1">No se comprará</p>
                  )}
                </div>
              </div>

              {/* 2. Ficha compacta */}
              <div className="mt-3 space-y-1 text-sm text-slate-700">
                {ficha.sku && (
                  <p>
                    <span className="font-semibold text-slate-500">SKU:</span>{' '}
                    <span className="font-bold text-slate-900">{ficha.sku}</span>
                  </p>
                )}
                {ficha.codigo_barras && (
                  <p>
                    <span className="font-semibold text-slate-500">Cód. barras:</span>{' '}
                    {ficha.codigo_barras}
                  </p>
                )}
                {ficha.marca && (
                  <p>
                    <span className="font-semibold text-slate-500">Marca:</span> {ficha.marca}
                  </p>
                )}
                <p>
                  <span className="font-semibold text-slate-500">Medidas:</span>{' '}
                  {ficha.medidas || 'S/Aplica'}
                </p>
                <p>
                  <span className="font-semibold text-slate-500">Aplicaciones:</span>{' '}
                  <span className="italic">{aplicacionesTxt || 'S/Aplica'}</span>
                </p>
              </div>

              {/* 3. Insights BI */}
              {!excluded && <OcAprobarInsight line={line} />}

              {/* 4. Precio + cantidad */}
              <p className={`mt-3 text-sm ${excluded ? 'text-slate-400 line-through' : 'text-slate-700'}`}>
                {formatCop(line.valor_unitario_estimado)} c/u
                {!excluded && (
                  <>
                    {' · '}
                    <span className="font-bold text-emerald-700">{formatCop(line.valor_linea)}</span>
                  </>
                )}
              </p>

              {!excluded && (
                <div className="mt-3 flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-slate-600">Cantidad</span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={saving || busy}
                      onClick={() => changeQty(line, -1)}
                      className="w-12 h-12 rounded-xl bg-slate-100 text-slate-800 flex items-center justify-center text-lg active:bg-slate-200 disabled:opacity-50"
                      aria-label="Bajar cantidad"
                    >
                      <FaMinus />
                    </button>
                    <input
                      type="number"
                      inputMode="decimal"
                      min="1"
                      step="1"
                      disabled={saving || busy}
                      defaultValue={line.cantidad}
                      key={`qty-${line.id_detalle}-${line.cantidad}`}
                      onBlur={(e) => setQty(line, e.target.value)}
                      className="w-16 h-12 text-center text-lg font-bold border border-slate-200 rounded-xl"
                    />
                    <button
                      type="button"
                      disabled={saving || busy}
                      onClick={() => changeQty(line, 1)}
                      className="w-12 h-12 rounded-xl bg-slate-100 text-slate-800 flex items-center justify-center text-lg active:bg-slate-200 disabled:opacity-50"
                      aria-label="Subir cantidad"
                    >
                      <FaPlus />
                    </button>
                  </div>
                </div>
              )}

              {/* 5. Quitar */}
              <button
                type="button"
                disabled={saving || busy}
                onClick={() => toggleExcluir(line)}
                className={`mt-3 w-full h-12 rounded-xl text-base font-bold border transition-colors disabled:opacity-50 ${
                  excluded
                    ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                    : 'bg-red-50 text-red-700 border-red-200'
                }`}
              >
                {excluded ? 'Deshacer — volver a incluir' : 'No comprar este'}
              </button>
            </article>
          );
        })}
      </main>

      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 px-3 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-[0_-4px_20px_rgba(0,0,0,0.06)] z-20">
        <div className="max-w-lg mx-auto flex gap-3">
          <button
            type="button"
            disabled={busy}
            onClick={() => setShowReject(true)}
            className="flex-1 h-14 rounded-2xl border-2 border-slate-300 text-slate-700 text-base font-bold active:bg-slate-50 disabled:opacity-50"
          >
            Rechazar todo
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={openAprobar}
            className="flex-[1.4] h-14 rounded-2xl bg-emerald-600 text-white text-lg font-bold shadow-sm active:bg-emerald-700 disabled:opacity-50"
          >
            Aprobar compra
          </button>
        </div>
      </div>

      {showApprove && (
        <div
          className="fixed inset-0 z-30 flex items-end sm:items-center justify-center p-0 sm:p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
        >
          <div className="bg-white w-full max-w-lg rounded-t-3xl sm:rounded-2xl p-6 space-y-4 shadow-xl">
            <h2 className="text-2xl font-bold text-slate-900">¿Confirmas la compra?</h2>
            <p className="text-base text-slate-700 leading-snug">
              <span className="font-semibold">{orden?.nombre_proveedor || 'Proveedor'}</span>
              {' · '}
              <span className="font-bold text-emerald-700">{formatCop(totalActivo)}</span>
            </p>
            <label className="block">
              <span className="text-base font-semibold text-slate-800">
                Notas u observaciones
                <span className="font-normal text-slate-500"> (opcional)</span>
              </span>
              <textarea
                value={observacionesAprobacion}
                onChange={(e) => setObservacionesAprobacion(e.target.value)}
                rows={5}
                placeholder="Escribe aquí si quieres dejar una nota…"
                className="mt-2 w-full min-h-[140px] text-lg border-2 border-slate-200 rounded-2xl p-4 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400"
              />
            </label>
            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={() => setShowApprove(false)}
                className="flex-1 h-14 rounded-2xl border-2 border-slate-300 text-base font-bold text-slate-700"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={confirmarAprobar}
                className="flex-[1.3] h-14 rounded-2xl bg-emerald-600 text-white text-lg font-bold disabled:opacity-50"
              >
                Sí, aprobar
              </button>
            </div>
          </div>
        </div>
      )}

      {showReject && (
        <div
          className="fixed inset-0 z-30 flex items-end sm:items-center justify-center p-0 sm:p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
        >
          <div className="bg-white w-full max-w-lg rounded-t-3xl sm:rounded-2xl p-6 space-y-4 shadow-xl">
            <h2 className="text-2xl font-bold text-slate-900">¿Por qué rechazas?</h2>
            <p className="text-base text-slate-600">
              El analista verá este motivo para corregir la orden.
            </p>
            <textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              rows={5}
              placeholder="Escribe el motivo del rechazo…"
              className="w-full min-h-[140px] text-lg border-2 border-slate-200 rounded-2xl p-4 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400"
            />
            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={() => setShowReject(false)}
                className="flex-1 h-14 rounded-2xl border-2 border-slate-300 text-base font-bold text-slate-700"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={rechazar}
                className="flex-[1.3] h-14 rounded-2xl bg-orange-600 text-white text-lg font-bold disabled:opacity-50"
              >
                Confirmar rechazo
              </button>
            </div>
          </div>
        </div>
      )}

      {fotoGrande && (
        <div
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
          role="dialog"
          aria-modal="true"
          aria-label="Foto del producto"
          onClick={() => setFotoGrande(null)}
        >
          <p className="text-white text-base font-semibold mb-3 text-center max-w-lg px-2 drop-shadow">
            {fotoGrande.nombre}
          </p>
          <img
            src={fotoGrande.url}
            alt={fotoGrande.nombre}
            className="max-w-full max-h-[70vh] object-contain rounded-lg bg-white shadow-lg"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setFotoGrande(null);
            }}
            className="mt-5 w-full max-w-xs h-14 rounded-2xl bg-white text-slate-900 text-lg font-bold shadow-md active:bg-slate-100"
          >
            Cerrar foto
          </button>
        </div>
      )}
    </div>
  );
};

export default OrdenCompraAprobarPublic;
